import { toast } from '@codes/ui-kit/components/ui/use-toast';
import { SaveSecretBody, WorkspaceBody, WorkspaceView } from '@lemoncloud/codes-goods-api';
import { Params } from '@lemoncloud/lemon-web-core';
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { PaginationType, useCustomMutation } from '@codes/shared';

import {
    createWorkspace,
    deleteWorkspace,
    enrollAccessKey,
    enrollAccessKeyWithWorkspaceId,
    fetchMyWorkspaces,
    fetchWorkspaceById,
    fetchWorkspaces,
    updateWorkspace,
    updateWorkspaceGeminiKey,
} from '../apis';
import { WORKSPACE_REFETCH_INTERVAL, workspacesKeys } from '../consts';
import { CreateWorkspaceDTO, UpdateWorkspaceDTO } from '../types';

export const useValidWorkspaces = () => {
    return useCustomMutation((siteId: string) => fetchMyWorkspaces({ siteId }), {
        onError: error => {
            {
                toast({ title: `${error?.toString()}` });
                return error;
            }
        },
    });
};

export const useMyWorkspaces = (params: Params = {}) =>
    useQuery<PaginationType<WorkspaceView[]>>({
        queryKey: workspacesKeys.list(params ?? {}),
        queryFn: async () => {
            const result = await fetchMyWorkspaces(params);
            return { ...result, data: result.list || [] } as PaginationType<WorkspaceView[]>;
        },
        refetchOnWindowFocus: false,
    });

export const useWorkspaces = (params: Params) =>
    useQuery<PaginationType<WorkspaceView[]>>({
        queryKey: workspacesKeys.list(params ?? {}),
        queryFn: async () => {
            const result = await fetchWorkspaces(params);
            return { ...result, data: result.list || [] } as PaginationType<WorkspaceView[]>;
        },
        refetchOnWindowFocus: false,
    });

export const useInfiniteWorkspaces = (params?: Params) => {
    return useInfiniteQuery({
        queryKey: workspacesKeys.list(params ?? {}),
        queryFn: ({ pageParam = 0 }) => fetchWorkspaces({ ...params, page: pageParam }),
        getNextPageParam: lastPage => {
            const { page, limit, total } = lastPage;

            const minLimit = Math.max(1, limit || 10);
            const safeTotal = total || 0;
            const safePage = page || 0;

            const maxPages = Math.ceil(safeTotal / minLimit);
            return safePage + 1 < maxPages ? safePage + 1 : undefined;
        },
        initialPageParam: 0,
    });
};

export const useGetWorkspace = () => {
    return useCustomMutation((workspaceId: string) => fetchWorkspaceById(workspaceId), {
        onError: error => {
            toast({ title: `${error.toString()}` });
        },
    });
};

export const useWorkspace = (workspaceId: string) =>
    useQuery<WorkspaceView>({
        queryKey: workspacesKeys.detail(workspaceId),
        queryFn: () => fetchWorkspaceById(workspaceId),
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        refetchInterval: data => (data.state.data?.state === 'requested' ? WORKSPACE_REFETCH_INTERVAL : false),
        enabled: !!workspaceId,
    });

export const useCreateWorkspace = () => {
    return useCustomMutation<WorkspaceView, AxiosError<string>, WorkspaceBody>(
        (data: CreateWorkspaceDTO) => createWorkspace(data),
        {
            onError: error => {
                toast({ title: `${error.response?.data}` });
            },
        }
    );
};

export const useUpdateWorkspace = () => {
    return useCustomMutation((data: UpdateWorkspaceDTO) => updateWorkspace(data), {
        onError: error => {
            toast({ title: `${error.toString()}` });
        },
    });
};

export const useDeleteWorkspace = () => {
    return useCustomMutation((id: string) => deleteWorkspace(id), {
        onError: error => {
            toast({ title: `${error.toString()}` });
        },
    });
};

export const useEnrollAccessKey = () => {
    return useCustomMutation((body: SaveSecretBody) => enrollAccessKey(body), {
        onError: error => {
            {
                toast({ title: `${error?.toString()}` });
                return error;
            }
        },
    });
};

export const useEnrollAccessKeyWithWorkspaceId = () => {
    return useCustomMutation(
        ({ body, workspaceId }: { body: SaveSecretBody; workspaceId: string }) =>
            enrollAccessKeyWithWorkspaceId(workspaceId, body),
        {
            onError: error => {
                toast({ title: `${error?.toString()}` });
            },
        }
    );
};

/**
 * Hook to update workspace Gemini API Key with optimistic UI
 */
export const useUpdateWorkspaceGeminiKey = () => {
    const queryClient = useQueryClient();

    return useCustomMutation(
        ({ workspaceId, geminiApiKey }: { workspaceId: string; geminiApiKey: string }) =>
            updateWorkspaceGeminiKey(workspaceId, geminiApiKey),
        {
            onMutate: async ({ workspaceId, geminiApiKey }) => {
                await queryClient.cancelQueries({ queryKey: workspacesKeys.detail(workspaceId) });
                const previousWorkspace = queryClient.getQueryData<WorkspaceView>(workspacesKeys.detail(workspaceId));
                queryClient.setQueryData<WorkspaceView>(workspacesKeys.detail(workspaceId), old => {
                    if (!old) return old;
                    return { ...old, geminiApiKey };
                });

                return { previousWorkspace };
            },
            onSuccess: () => {
                toast({
                    title: 'Success',
                    description: 'Gemini API Key has been updated',
                });
            },
            onError: (error, { workspaceId }, context) => {
                if (context?.previousWorkspace) {
                    queryClient.setQueryData(workspacesKeys.detail(workspaceId), context.previousWorkspace);
                }

                toast({
                    title: 'Error',
                    description: error?.toString(),
                    variant: 'destructive',
                });
            },
            onSettled: (_data, _error, { workspaceId }) => {
                queryClient.invalidateQueries({ queryKey: workspacesKeys.detail(workspaceId) });
            },
        }
    );
};
